import { Preferences } from '@capacitor/preferences';
import { useState, useEffect, useCallback, SetStateAction } from 'react';

export interface PersistentStorage<T> {
  value: T | undefined;
  setValue: (valOrFunc: SetStateAction<T | undefined>) => Promise<void> | null;
  removeValue: () => Promise<void>;
  loading: boolean;
}

/**
 * A hook for storing key-value pairs in persistent storage that is compatible
 * with web and native.
 * When using localStorage on iOS and Android, the stored values are not
 * guaranteed to persist across app restarts. If that is required, use this hook
 * instead.
 *
 * @param key - The unique key to store the value under
 * @param initialValue - Optional initial value to use if no stored value exists
 * @returns A tuple containing:
 *  - The current value (undefined if not set)
 *  - A function to update the value
 *  - A function to remove the value
 *  - A boolean indicating if the initial load is in progress
 */
export const usePersistentStorage = <T>(
  key: string,
  initialValue?: T
): PersistentStorage<T> => {
  const [storedValue, setStoredValue] = useState<T | undefined>(initialValue);
  const [loading, setLoading] = useState<boolean>(true);

  useEffect(() => {
    let isMounted = true;

    const fetchStoredValue = async () => {
      try {
        const { value } = await Preferences.get({ key });
        if (!isMounted) return;

        if (value !== null) {
          // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
          setStoredValue(JSON.parse(value));
        } else if (initialValue !== undefined) {
          await Preferences.set({ key, value: JSON.stringify(initialValue) });
          setStoredValue(initialValue);
        }
      } catch {
        if (isMounted) setStoredValue(initialValue);
      } finally {
        if (isMounted) setLoading(false);
      }
    };

    void fetchStoredValue();

    return () => {
      isMounted = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const setValue = useCallback((valOrFunc: SetStateAction<T | undefined>) => {
    const newValue =
      typeof valOrFunc === 'function'
        ? (valOrFunc as (prev: T | undefined) => T | undefined)(storedValue)
        : valOrFunc;
    if (newValue === undefined) {
      setStoredValue(newValue);
      return null;
    }
    const valueToStore =
      typeof newValue === 'string' ? newValue : JSON.stringify(newValue);
    return Preferences.set({ key, value: valueToStore }).then(() => {
      setStoredValue(newValue);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const removeValue = useCallback(() => {
    return Preferences.remove({ key }).then(() => {
      setStoredValue(undefined);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return {
    value: storedValue,
    setValue: setValue,
    removeValue: removeValue,
    loading,
  };
};
